import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useDemo } from '../../contexts/DemoContext';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { ScenarioKeyTable } from '../../components/demo/ScenarioKeyTable';
import { pipelineStages } from '../../data/demo';
const STAGE_MS = [1400, 1500, 2000, 1800, 1500, 1600];
function ConsistencyGrid() {
  // 3 models × 10 runs. One amber dot is the 9/10.
  const dots = Array.from({
    length: 30
  }, (_, i) => i);
  return <div data-id="element-675" data-mp="1j0nvehh4y19r">
      <div className="grid w-fit grid-cols-10 gap-1.5" data-id="element-676" data-mp="1j0xpag5awskq">
        {dots.map(i => <span key={i} className={`h-2 w-2 rounded-sm ${i === 7 ? 'bg-signal-amber' : 'bg-brand/70'}`} data-id="element-677" data-mp="3id9siw8dmxvn" />)}
      </div>
      <p className="mt-3 text-[12.5px] leading-relaxed text-white/60" data-id="element-678" data-mp="3df88o934muj4">
        Below 80% consistency, a scenario is rebuilt. This one held at 90.
      </p>
    </div>;
}
export function Pipeline() {
  const {
    mode,
    cvName,
    setPipelineComplete
  } = useDemo();
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const injectRef = useRef<HTMLLIElement>(null);
  const requestedKey = (location.state as {
    openKey?: boolean;
  } | null)?.openKey === true;
  const [keyShown, setKeyShown] = useState(false);
  useEffect(() => {
    if (active >= pipelineStages.length) {
      setPipelineComplete(true);
      return;
    }
    const t = window.setTimeout(() => setActive(a => a + 1), STAGE_MS[active]);
    return () => window.clearTimeout(t);
  }, [active, setPipelineComplete]);

  // Arriving from the configuration screen's "full key" link: open stage ② once it has run.
  useEffect(() => {
    if (!requestedKey || keyShown || active <= 1) return;
    setKeyShown(true);
    setOpen(1);
    window.setTimeout(() => injectRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    }), 80);
  }, [requestedKey, keyShown, active]);
  const complete = active >= pipelineStages.length;
  return <div className="flex min-h-full w-full flex-col bg-ink" data-id="element-679" data-mp="1arau081auund">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-680" data-mp="16nbi62qysk0c">
        <LenticMark dark data-id="element-681" data-mp="11p1m7pxf1fdo" />
        <BackLink to="/demo/configure" label="Back to configuration" dark data-id="element-682" data-mp="11qcqdqurwp5b" />
      </div>

      <main className="mx-auto w-full max-w-[860px] flex-1 px-8 py-14" data-id="element-683" data-mp="wx0caanq00qt">
        <p className="eyebrow text-brand" data-id="element-684" data-mp="ryfcgccdrj2w">Build &amp; gate · FIN-001 draft</p>
        <h1 className="mt-4 text-[26px] font-semibold tracking-tight text-white" data-id="element-685" data-mp="mzvqwd1p89b2">
          Built, then made to prove itself.
        </h1>

        <ol className="mt-10 divide-y divide-white/10 border-y border-white/10" data-id="element-686" data-mp="ix8sftaivq4w">
          {pipelineStages.map((stage, i) => {
          const state = i < active ? 'done' : i === active ? 'running' : 'pending';
          const isOpen = open === i;
          const stageMeta = i === 0 && mode === 'single' && cvName ? 'selection weighted by claimed experience · content from gated stock' : stage.meta;
          return <li key={stage.name} ref={i === 1 ? injectRef : undefined} data-id="element-687" data-mp="33mtz9p279nmc" data-mpi={i}>
                <button type="button" disabled={state !== 'done'} onClick={() => setOpen(isOpen ? null : i)} className="grid w-full grid-cols-[28px_150px_minmax(0,1fr)_auto] items-start gap-4 py-5 text-left disabled:cursor-default" data-id="element-688" data-mp="33jy0xzmfwwxd">
                  <span className="pt-0.5" data-id="element-689" data-mp="2knzjqduwwj54">
                    {state === 'done' ? <span className="flex h-5 w-5 items-center justify-center rounded-sm bg-brand" data-id="element-690" data-mp="1n8bwuzq3mniq">
                        <CheckIcon className="h-3 w-3 text-white" strokeWidth={3} data-id="element-691" data-mp="1s6vmpb7tcjgd" />
                      </span> : state === 'running' ? <span className="flex h-5 w-5 animate-pulse items-center justify-center rounded-sm border border-signal-amber/70 bg-signal-amber/20" data-id="element-692" data-mp="3uu283wztl71i">
                        <span className="h-1.5 w-1.5 rounded-full bg-signal-amber" data-id="element-693" data-mp="7gzvkb58q3me" />
                      </span> : <span className="flex h-5 w-5 items-center justify-center rounded-sm border border-white/15" data-id="element-694" data-mp="2oqd1g6gg5u5" />}
                  </span>

                  <span data-id="element-695" data-mp="cgmdgxw0k81j">
                    <span className={`block text-[14px] font-medium ${state === 'pending' ? 'text-white/25' : 'text-white'}`} data-id="element-696" data-mp="cfb5smzg6g55">
                      <span className="mr-2 font-mono-num text-white/40" data-id="element-697" data-mp="mbwblajajva1">{stage.index}</span>
                      {stage.name}
                    </span>
                    <span className={`mt-0.5 block text-[12px] ${state === 'pending' ? 'text-white/15' : 'text-white/45'}`} data-id="element-698" data-mp="2o6f5zfxsv72c">
                      {stage.detail}
                    </span>
                  </span>

                  <motion.span initial={false} animate={{
                opacity: state === 'pending' ? 0 : 1,
                y: state === 'pending' ? 4 : 0
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className={`font-mono-num text-[11.5px] leading-relaxed ${state === 'running' ? 'text-signal-amber' : 'text-brand-light'}`} data-id="element-699" data-mp="24akni5x2oa16">
                    {state === 'running' ? 'running…' : stageMeta}
                  </motion.span>

                  {state === 'done' && <ChevronDownIcon className={`h-4 w-4 text-white/30 transition-transform duration-150 ease-settle ${isOpen ? 'rotate-180' : ''}`} data-id="element-700" data-mp="3fru5eo0jyu0d" />}
                </button>

                <AnimatePresence initial={false} data-id="element-701" data-mp="3e9dm783fxnq2">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden" data-id="element-702" data-mp="39aadndo94zdc">
                      <div className="ml-[44px] mb-6 border-l border-white/10 pl-5" data-id="element-703" data-mp="viko2s4i2uw">
                        {i === 1 ? <div className="max-w-[760px]" data-id="element-704" data-mp="3nabhn1ztcmx1">
                            <p className="font-mono-num text-[11px] text-white/50" data-id="element-705" data-mp="3n9s9gczytcw7">
                              Written at injection. Sealed from candidates.
                            </p>
                            <div className="mt-4" data-id="element-706" data-mp="jstbr03lppz9">
                              <ScenarioKeyTable dark data-id="element-707" data-mp="r4r8zq2ra09n" />
                            </div>
                          </div> : i === 2 ? <ConsistencyGrid data-id="element-708" data-mp="w4cdsh4lx2ua" /> : stage.manifest ? <div data-id="element-709" data-mp="111cc25v7t1au">
                            <p className="eyebrow text-white/40" data-id="element-710" data-mp="1159x4x1cs2xd">Manifest</p>
                            <ul className="mt-3 space-y-1.5" data-id="element-711" data-mp="163bhrmefgaay">
                              {stage.manifest.map(m => <li key={m.component} className="flex items-baseline gap-4 font-mono-num text-[11.5px]" data-id="element-712" data-mp="1a1r7hy1w7ws2">
                                  <span className="w-64 text-white/70" data-id="element-713" data-mp="1ot19u1qt2hz6">{m.component}</span>
                                  <span className="text-brand-light" data-id="element-714" data-mp="1trupo0tkel2i">{m.hash}</span>
                                </li>)}
                            </ul>
                            <p className="mt-3 text-[12.5px] leading-relaxed text-white/60" data-id="element-715" data-mp="2j44orbx4mkdr">
                              {stage.drawerCaption}
                            </p>
                          </div> : <p className="max-w-xl text-[12.5px] leading-relaxed text-white/60" data-id="element-716" data-mp="2t2kebagi9ll5">
                            {stage.drawerCaption}
                          </p>}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>;
        })}
        </ol>

        <AnimatePresence data-id="element-717" data-mp="2a3gaqh7qgk0v">
          {complete && <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-10 border-l-2 border-brand pl-6" data-id="element-718" data-mp="2a514i4m83yxo">
              <h2 className="text-[20px] font-semibold tracking-tight text-white" data-id="element-719" data-mp="1coklskjzoy7x">
                Generated, but governed.
              </h2>
              <p className="mt-2 max-w-xl text-[13.5px] leading-relaxed text-white/60" data-id="element-720" data-mp="3o9ur5y7c2air">
                Every check you just watched runs on every assessment, every time — and re-runs
                against every new frontier model release.
              </p>
              <button type="button" onClick={() => navigate('/demo/instrument')} className="group mt-7 inline-flex items-center gap-2 rounded-sm bg-brand px-6 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark" data-id="element-721" data-mp="3jbjk1v8fae48">
                Review the instrument
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-settle group-hover:translate-x-0.5" data-id="element-722" data-mp="6uweukstrjt8" />
              </button>
            </motion.div>}
        </AnimatePresence>

        <p className="mt-16 font-mono-num text-[11px] text-white/30" data-id="element-723" data-mp="2rkvlmqy4vmu9">
          Demo build · pre-authored pipeline output · production runs live gates
        </p>
      </main>

      <DemoFooter dark data-id="element-724" data-mp="18012o2rsp7wj" />
    </div>;
}